import { z } from 'zod';

/**
 * Role templates. A membership carries a role template PLUS granular
 * permission overrides; the template is only a starting set (spec §4).
 */
export const RoleTemplate = z.enum([
  'platform_owner',
  'clinic_owner',
  'clinic_admin',
  'dentist',
  'receptionist',
  'accountant',
  'inventory_staff',
  'lab_staff',
  'external_partner',
]);
export type RoleTemplate = z.infer<typeof RoleTemplate>;

/**
 * Granular permissions. Deliberately verb-scoped so a receptionist's
 * scheduling access never implies medical notes or financial reports.
 */
export const Permission = z.enum([
  // patients
  'patient.read.assigned',
  'patient.read.all',
  'patient.write',
  'patient.archive',
  'patient.export',
  'patient.merge',
  /*
   * Removing a patient ENTIRELY, as distinct from archiving them.
   * Separate from `patient.archive` because they are different acts: archiving
   * hides someone who stopped coming and is reversible; deletion is for a
   * record created in error and cannot be undone. The endpoint refuses outright
   * once clinical or financial history exists, so this permission grants the
   * ability to clean up mistakes, not to erase history.
   */
  'patient.delete',
  // clinical
  'clinical.chart.read',
  'clinical.chart.write',
  'clinical.notes.read',
  'clinical.notes.write',
  'clinical.notes.amend',
  /*
   * Every dentist's notes, including the ones they kept private, with who
   * wrote them. For the people who run the clinic; a dentist sees their own
   * notes and the ones other dentists chose to share (without the author).
   */
  'clinical.notes.read_all',
  'medical_history.read',
  'medical_history.write',
  // imaging
  'imaging.read',
  'imaging.upload',
  'imaging.acquire',
  'imaging.download_original',
  'imaging.delete',
  // prescriptions
  'rx.draft',
  'rx.issue',
  'rx.template.manage',
  // appointments
  'appointment.read',
  'appointment.write',
  'appointment.cancel',
  'schedule.manage',
  // finance
  'finance.patient_account.read',
  'finance.invoice.create',
  'finance.invoice.post',
  'finance.payment.record',
  'finance.refund',
  'finance.writeoff',
  'finance.discount',
  'finance.reports.read',
  'finance.reports.own_only',
  'finance.expense.manage',
  /* Discarding a DRAFT invoice. A posted one is reversed by credit note. */
  'finance.invoice.delete',
  /* Reversing a payment that was recorded in error. */
  'finance.payment.void',
  // dentists
  'dentist.manage',
  'commission.read.own',
  'commission.read.all',
  'commission.settle',
  // lab
  'lab.read',
  'lab.write',
  'lab.finance',
  /* Discarding a lab order that has not yet been sent to the laboratory. */
  'lab.order.delete',
  // inventory
  'inventory.read',
  'inventory.write',
  'inventory.adjust',
  'supplier.manage',
  'purchase.post',
  'purchase.receive',
  /* Discarding a DRAFT purchase order, before it has moved any stock. */
  'purchase.delete',
  // reports
  /* The clinic's reports: new patients and their sources, every dentist's activity, missed appointments. */
  'reports.read',
  /* The same reports limited to the caller: their own patients, procedures and missed appointments. */
  'reports.own',
  // clinic management
  'clinic.settings.manage',
  'clinic.staff.manage',
  'clinic.audit.read',
  'clinic.import',
  'clinic.public_page.manage',
  'clinic.device.manage',
  // platform
  'platform.clinic.manage',
  'platform.subscription.manage',
  'platform.activation_code.issue',
  'platform.support_access',
]);
export type Permission = z.infer<typeof Permission>;

export const ALL_PERMISSIONS = Permission.options;
const CLINIC_PERMISSIONS = ALL_PERMISSIONS.filter((p) => !p.startsWith('platform.'));

export const MembershipStatus = z.enum(['invited', 'active', 'suspended', 'removed']);
export type MembershipStatus = z.infer<typeof MembershipStatus>;

export const ROLE_TEMPLATE_PERMISSIONS: Record<RoleTemplate, readonly Permission[]> = {
  platform_owner: [
    'platform.clinic.manage',
    'platform.subscription.manage',
    'platform.activation_code.issue',
    'platform.support_access',
  ],
  clinic_owner: CLINIC_PERMISSIONS,
  /*
   * An administrator runs the clinic day to day but is kept away from the acts
   * that destroy evidence: merging two patients, deleting one, and deleting a
   * radiograph. An owner can still delegate any of them explicitly.
   */
  clinic_admin: CLINIC_PERMISSIONS.filter(
    (p) => p !== 'patient.merge' && p !== 'imaging.delete' && p !== 'patient.delete',
  ),
  dentist: [
    'patient.read.assigned',
    'patient.write',
    'clinical.chart.read',
    'clinical.chart.write',
    'clinical.notes.read',
    'clinical.notes.write',
    'clinical.notes.amend',
    'medical_history.read',
    'medical_history.write',
    'imaging.read',
    'imaging.upload',
    'imaging.acquire',
    'imaging.download_original',
    'rx.draft',
    'rx.issue',
    'rx.template.manage',
    'appointment.read',
    'appointment.write',
    'lab.read',
    'lab.write',
    'commission.read.own',
    'finance.reports.own_only',
    'reports.own',
  ],
  receptionist: [
    'patient.read.all',
    'patient.write',
    'appointment.read',
    'appointment.write',
    'appointment.cancel',
    'schedule.manage',
    'imaging.read',
    'imaging.upload',
  ],
  accountant: [
    'patient.read.all',
    'finance.patient_account.read',
    'finance.invoice.create',
    'finance.invoice.post',
    'finance.payment.record',
    'finance.refund',
    'finance.writeoff',
    'finance.discount',
    'finance.reports.read',
    'finance.expense.manage',
    'commission.read.all',
    'commission.settle',
    'lab.finance',
    'supplier.manage',
    'purchase.post',
  ],
  inventory_staff: [
    'inventory.read',
    'inventory.write',
    'inventory.adjust',
    'supplier.manage',
    'purchase.receive',
  ],
  lab_staff: ['lab.read', 'lab.write', 'patient.read.all'],
  external_partner: [],
};

/**
 * Effective permissions for a membership: template set, plus explicit grants,
 * minus explicit revocations. Revocation always wins so a narrowing override
 * cannot be defeated by a template change.
 */
export function effectivePermissions(input: {
  roles: readonly RoleTemplate[];
  granted?: readonly Permission[];
  revoked?: readonly Permission[];
}): Set<Permission> {
  const out = new Set<Permission>();
  for (const role of input.roles) for (const p of ROLE_TEMPLATE_PERMISSIONS[role]) out.add(p);
  for (const p of input.granted ?? []) out.add(p);
  for (const p of input.revoked ?? []) out.delete(p);
  return out;
}

/**
 * A user may never grant a permission they do not themselves hold (spec §4).
 * Returns the subset the grantor is not allowed to delegate.
 */
export function undelegatablePermissions(
  grantorPermissions: ReadonlySet<Permission>,
  requested: readonly Permission[],
): Permission[] {
  return requested.filter((p) => !grantorPermissions.has(p));
}
